import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { ArrowLeftIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { contractApi } from '../services/api';
import ContractSigner from '../components/ContractSigner';

const SignContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContract = async () => {
      try {
        setLoading(true);
        const response = await contractApi.getById(id);
        setContract(response.data);
      } catch (error) {
        console.error('Failed to fetch contract:', error);
        toast.error('Failed to load contract');
        navigate('/contracts');
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
  }, [id, navigate]);

  const handleDownloadPDF = async () => {
    try {
      await contractApi.generatePDF(id);
      toast.success('PDF generated successfully');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleSigningComplete = () => {
    toast.success('Contract successfully signed!');
    // Refresh contract data to get updated signature status
    contractApi.getById(id).then(response => {
      setContract(response.data);
    });
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Check if fully signed
  const isFullySigned = contract?.landlordSigned && contract?.tenantSigned;

  return (
    <div>
      <div className="flex items-center mb-6">
        <Link to={`/contracts/${id}`} className="text-gray-600 hover:text-gray-900 mr-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          Sign Contract
        </h1>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading contract details...</div>
      ) : contract ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contract details column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Details</h2>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Property Address</p>
                  <p className="font-medium">{contract.propertyAddress}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Landlord</p>
                  <p className="font-medium">{contract.landlordName}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Tenant</p>
                  <p className="font-medium">{contract.tenantFullName}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Contract Start Date</p>
                  <p className="font-medium">{formatDate(contract.contractStartDate)}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium">{contract.contractDuration} year(s)</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Monthly Rent</p>
                  <p className="font-medium">{contract.monthlyRent?.toFixed(2)} €</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      contract.status === 'active' ? 'bg-green-100 text-green-800' :
                      contract.status === 'signed' ? 'bg-blue-100 text-blue-800' :
                      contract.status === 'pending_signature' ? 'bg-yellow-100 text-yellow-800' :
                      contract.status === 'terminated' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {contract.status === 'draft' ? 'Brouillon' :
                       contract.status === 'pending_signature' ? 'En attente de signature' :
                       contract.status === 'signed' ? 'Signé' :
                       contract.status === 'active' ? 'Actif' :
                       contract.status === 'terminated' ? 'Résilié' : contract.status}
                    </span>
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Signatures</p>
                  <div className="mt-1 space-y-1 text-sm">
                    <p className="font-medium">
                      Landlord: {contract.landlordSigned ?
                        <span className="text-green-600">✓ Signed</span> :
                        <span className="text-amber-600">Pending</span>
                      }
                    </p>
                    <p className="font-medium">
                      Tenant: {contract.tenantSigned ?
                        <span className="text-green-600">✓ Signed</span> :
                        <span className="text-amber-600">Pending</span>
                      }
                    </p>
                  </div>
                </div>
              </div>

              {isFullySigned && (
                <div className="mt-6">
                  <button
                    onClick={handleDownloadPDF}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Generate & Download PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Signing column */}
          <div className="lg:col-span-2">
            {isFullySigned ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Contract Fully Signed</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This contract has been signed by both landlord and tenant.
                    You can download the signed contract PDF or activate the contract.
                  </p>
                </div>
              </div>
            ) : (
              <ContractSigner
                contract={contract}
                onSigningComplete={handleSigningComplete}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">Contract not found</div>
      )}
    </div>
  );
};

export default SignContract;
